import { useController, useWatch } from 'react-hook-form';
import { LabeledContainer, Select } from '@schibsted-svp/react-ui';
import { MediaLiveInputsQuery } from 'services/admin-bff-sdk/generated';
import { MediaLiveInputType } from 'services/admin-bff/contants';
import { getValueFromOption, findSingleOptionInOptionsGroup } from 'components/ui/Form/Select';
import { useMediaLiveInputFormContext } from './hooks';

type MediaLiveInput = MediaLiveInputsQuery['mediaLiveInputs'][number];

const customOptionStyles = (
    provided: Record<string, string>,
    {
        data,
    }: {
        data: MediaLiveInputOption;
    }
) => {
    if (data.occupied) {
        return {
            ...provided,
            color: '#dfc552 !important',
            fontStyle: 'italic',
        };
    }
    return provided;
};

const isMediaLiveInputOccupied = (input: MediaLiveInput): boolean => {
    return Boolean(input.channel?.asset);
};

const getMediaLiveInputOptionLabel = (input: MediaLiveInput): string => {
    if (isMediaLiveInputOccupied(input)) {
        return `${input.name} (occupied: ${input.channel.asset.id})`;
    }
    return input.name;
};

type MediaLiveInputOption = {
    value: string;
    label: string;
    occupied: boolean;
};

const getMediaLiveInputOptions = (
    inputs: MediaLiveInput[],
    inputType: keyof typeof MediaLiveInputType
): MediaLiveInputOption[] => {
    const options = inputs.map((input) => ({
        label: getMediaLiveInputOptionLabel(input),
        value: input.id,
        occupied: isMediaLiveInputOccupied(input),
    }));

    // Elemental Link inputs are provisioned outside of admin, so there is nothing to create here
    if (inputType === MediaLiveInputType.LINK) {
        return options;
    }

    return [
        ...options,
        {
            label: inputType === MediaLiveInputType.PULL ? 'Custom URL' : 'Create new input',
            value: '',
            occupied: false,
        },
    ];
};

type MediaLiveInputsSelectProps = {
    inputs: MediaLiveInput[];
};

export function MediaLiveInputsSelect({ inputs }: MediaLiveInputsSelectProps) {
    const { formApi } = useMediaLiveInputFormContext();
    const { control } = formApi;

    const inputType = useWatch({ name: 'inputType', control });
    const isLinkInput = inputType === MediaLiveInputType.LINK;

    const { field, fieldState } = useController({
        name: 'inputId',
        control,
        shouldUnregister: true,
        defaultValue: '',
        rules: isLinkInput ? { required: 'Field is required' } : {},
    });

    const options = getMediaLiveInputOptions(inputs, inputType);

    return (
        <LabeledContainer label="inputs">
            <Select
                {...field}
                value={findSingleOptionInOptionsGroup(options)(field.value)}
                onChange={(value) => field.onChange(getValueFromOption(value))}
                options={options}
                styles={{ option: customOptionStyles }}
                placeholder="Choose input"
                isSearchable={false}
                error={fieldState.error?.message}
            />
        </LabeledContainer>
    );
}
